import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { MessageChannel } from './communication-config';

/**
 * Messaging rules: which channels each message TYPE is delivered on, per scope.
 *
 * Rules are STRICT — a message goes out only on channels explicitly enabled here. There is no
 * SMS fallback. A partner/client with no rules for a type inherits the GLOBAL row.
 *
 * Responses may be PascalCase (Dapper dynamic) or camelCase (typed DTO), so reads are normalized
 * defensively, as in services/global-master.ts.
 */

export type RuleScopeType = 'GLOBAL' | 'PARTNER' | 'CLIENT' | 'ADMIN';

export const RULE_SCOPE_TYPES: { label: string; value: RuleScopeType }[] = [
  { label: 'Global defaults', value: 'GLOBAL' },
  { label: 'Partner', value: 'PARTNER' },
  { label: 'Client', value: 'CLIENT' },
  // Internal alerts to our own team — a different audience, so a different event list.
  { label: 'Admin alerts', value: 'ADMIN' },
];

/** GLOBAL and ADMIN carry no owner id, so the owner picker is hidden for them. */
export const isSingletonScope = (s: RuleScopeType) => s === 'GLOBAL' || s === 'ADMIN';

/** An Admin account and whether it currently receives alerts. Contacts come from the
 *  user record, so there is no second copy of an address to keep in sync. */
export interface AdminRecipient {
  userId: number;
  username: string;
  email: string | null;
  mobile: string | null;
  isSelected: boolean;
}

/** An extra number/address for an admin, beyond the one on their user record. */
export interface AdminUserContact {
  id?: number;
  userId: number;
  channel: MessageChannel;
  contact: string;
  label?: string | null;
  isActive: boolean;
}

/** A selectable owner in the scope picker. */
export interface RuleScopeRow {
  scopeType: RuleScopeType;
  scopeId: number;
  name: string;
  code: string | null;
  /** How many types this owner overrides; 0 => fully inherited from global. */
  overriddenTypes: number;
}

export interface RuleTypeRow {
  messageType: string;
  name: string | null;
  description: string | null;
  /** LOGIN: rendered, always on, not editable — disabling it would lock users out. */
  isLocked: boolean;
  /** True => this scope has no rows of its own and is showing global's values. */
  inherited: boolean;
  channels: Record<string, boolean>;
}

export interface RuleMatrix {
  scopeType: RuleScopeType;
  scopeId: number;
  scopeName: string;
  channels: MessageChannel[];
  types: RuleTypeRow[];
  /** Per channel: is a provider actually configured? A warning, never a block. */
  providerConfigured: Record<string, boolean>;
}

export interface RuleTypeDto {
  messageType: string;
  inherit: boolean;
  channels: Record<string, boolean>;
}

export interface RuleMatrixDto {
  scopeType: RuleScopeType;
  scopeId: number;
  types: RuleTypeDto[];
  /** Required to save a GLOBAL matrix that leaves a type with no channels. */
  confirmGlobalSilence?: boolean;
}

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}

/** Channel maps come back with whatever casing the serializer used; normalize the KEYS too. */
function upperKeys(o: any): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const k of Object.keys(o ?? {})) out[k.toUpperCase()] = !!o[k];
  return out;
}

function mapTypeRow(x: any): RuleTypeRow {
  return {
    messageType: pick(x, 'MessageType', 'messageType') ?? '',
    name: pick(x, 'Name', 'name') ?? null,
    description: pick(x, 'Description', 'description') ?? null,
    isLocked: !!pick(x, 'IsLocked', 'isLocked'),
    inherited: !!pick(x, 'Inherited', 'inherited'),
    channels: upperKeys(pick(x, 'Channels', 'channels')),
  };
}

function mapMatrix(x: any): RuleMatrix {
  return {
    scopeType: (pick(x, 'ScopeType', 'scopeType') ?? 'GLOBAL') as RuleScopeType,
    scopeId: Number(pick(x, 'ScopeId', 'scopeId') ?? 0),
    scopeName: pick(x, 'ScopeName', 'scopeName') ?? '',
    channels: (pick<any[]>(x, 'Channels', 'channels') ?? []).map((c) => String(c).toUpperCase() as MessageChannel),
    types: (pick<any[]>(x, 'Types', 'types') ?? []).map(mapTypeRow),
    providerConfigured: upperKeys(pick(x, 'ProviderConfigured', 'providerConfigured')),
  };
}

function mapScopeRow(x: any): RuleScopeRow {
  return {
    scopeType: (pick(x, 'ScopeType', 'scopeType') ?? 'PARTNER') as RuleScopeType,
    scopeId: Number(pick(x, 'ScopeId', 'scopeId') ?? 0),
    name: pick(x, 'Name', 'name') ?? '',
    code: pick(x, 'Code', 'code') ?? null,
    overriddenTypes: Number(pick(x, 'OverriddenTypes', 'overriddenTypes') ?? 0),
  };
}

function mapMatrixEnv(res: any) {
  return res?.data ? { ...res, data: mapMatrix(res.data) } : res;
}

function mapScopesEnv(res: any) {
  return Array.isArray(res?.data) ? { ...res, data: res.data.map(mapScopeRow) } : { ...res, data: [] };
}

@Injectable({ providedIn: 'root' })
export class MessageRuleService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/MessageRule`;

  /** Owners to pick from, with an "overrides N types" badge. */
  getScopes(scopeType: RuleScopeType, search?: string | null) {
    const params: Record<string, string> = { scopeType };
    if (search) params['search'] = search;
    return this.http.get(`${this.baseUrl}/GetScopes`, { params }).pipe(map(mapScopesEnv));
  }

  /** The whole matrix for a scope, in one call. */
  getMatrix(scopeType: RuleScopeType, scopeId: number) {
    return this.http
      .get(`${this.baseUrl}/GetMatrix`, { params: { scopeType, scopeId } })
      .pipe(map(mapMatrixEnv));
  }

  /** Save the whole matrix in one call — a matrix is a unit, not a set of independent cells. */
  saveMatrix(dto: RuleMatrixDto) {
    return this.http.post(`${this.baseUrl}/SaveMatrix`, dto).pipe(map(mapMatrixEnv));
  }

  /** Drop a scope's overrides so it falls back to global. Omit messageType to reset every type. */
  resetToInherit(scopeType: RuleScopeType, scopeId: number, messageType?: string | null) {
    const params: Record<string, string | number> = { scopeType, scopeId };
    if (messageType) params['messageType'] = messageType;
    return this.http.get(`${this.baseUrl}/ResetToInherit`, { params }).pipe(map(mapMatrixEnv));
  }

  /** What would actually be delivered right now, and why. */
  preview(scopeType: RuleScopeType, scopeId: number, messageType: string) {
    return this.http.get(`${this.baseUrl}/Preview`, { params: { scopeType, scopeId, messageType } });
  }

  health() {
    return this.http.get(`${this.baseUrl}/Health`);
  }

  /** Every Admin account, flagged with whether it currently receives alerts. */
  getRecipients() {
    return this.http.get(`${this.baseUrl}/GetRecipients`).pipe(
      map((res: any) =>
        Array.isArray(res?.data)
          ? {
              ...res,
              data: res.data.map((x: any) => ({
                userId: Number(pick(x, 'UserId', 'userId') ?? 0),
                username: pick(x, 'Username', 'username') ?? '',
                email: pick(x, 'Email', 'email') ?? null,
                mobile: pick(x, 'Mobile', 'mobile') ?? null,
                isSelected: (pick(x, 'IsSelected', 'isSelected') ?? false) === true,
              })) as AdminRecipient[],
            }
          : { ...res, data: [] },
      ),
    );
  }

  /** Replaces the whole selection — same unit-of-change reasoning as the matrix itself. */
  saveRecipients(userIds: number[]) {
    return this.http.post(`${this.baseUrl}/SaveRecipients`, { userIds });
  }

  /** Extra contacts for one admin, beyond the single email/mobile on their user record. */
  getUserContacts(userId: number) {
    return this.http.get(`${this.baseUrl}/GetUserContacts`, { params: { userId } }).pipe(
      map((res: any) =>
        Array.isArray(res?.data)
          ? {
              ...res,
              data: res.data.map((x: any) => ({
                id: pick(x, 'Id', 'id'),
                userId: Number(pick(x, 'UserId', 'userId') ?? 0),
                channel: String(pick(x, 'Channel', 'channel') ?? 'SMS').toUpperCase() as MessageChannel,
                contact: pick(x, 'Contact', 'contact') ?? '',
                label: pick(x, 'Label', 'label') ?? null,
                isActive: (pick(x, 'IsActive', 'isActive') ?? true) === true,
              })) as AdminUserContact[],
            }
          : { ...res, data: [] },
      ),
    );
  }

  saveUserContacts(userId: number, contacts: AdminUserContact[]) {
    return this.http.post(`${this.baseUrl}/SaveUserContacts`, { userId, contacts });
  }
}
